import { SheetGeometryCategory } from "./sheetGeometryCategory";

export interface SheetGeometryCategoryUIDescriptor {
  readonly type: "STYLE_ENABLED" | "SELECT_ENABLED";
  readonly category: SheetGeometryCategory;
}

function styleEnabledSubCategoryNew(category: keyof typeof SheetGeometryCategory): SheetGeometryCategoryUIDescriptor {
  return { type: "STYLE_ENABLED", category: SheetGeometryCategory[category] };
}

function selectEnabledSubCategoryNew(category: keyof typeof SheetGeometryCategory): SheetGeometryCategoryUIDescriptor {
  return { type: "SELECT_ENABLED", category: SheetGeometryCategory[category] };
}

/**
 * Maps sheet geometry categories to their UI descriptors.
 *
 * Providing a descriptor for a category will display it in the UI.
 */
export const sheetGeometryCategoryUIDescriptorMap: Record<
  SheetGeometryCategory,
  SheetGeometryCategoryUIDescriptor | undefined
> = {
  ANNOT_DIM_LABEL: styleEnabledSubCategoryNew("ANNOT_DIM_LABEL"),
  ANNOT_DIM_LINEWORK: styleEnabledSubCategoryNew("ANNOT_DIM_LINEWORK"),
  ANNOT_DIM_TICK: styleEnabledSubCategoryNew("ANNOT_DIM_TICK"),
  ANNOT_DIM_WITNESS: styleEnabledSubCategoryNew("ANNOT_DIM_WITNESS"),

  ANNOT_DETAIL: selectEnabledSubCategoryNew("ANNOT_DETAIL"),
  ANNOT_LINEWORK: selectEnabledSubCategoryNew("ANNOT_LINEWORK"),
  ANNOT_NOTE: selectEnabledSubCategoryNew("ANNOT_NOTE"),

  SLAB_TAG: selectEnabledSubCategoryNew("SLAB_TAG"),
  SLAB_ABOVE: selectEnabledSubCategoryNew("SLAB_ABOVE"),
  SLAB_BELOW: selectEnabledSubCategoryNew("SLAB_BELOW"),
  SLAB_PATIO: selectEnabledSubCategoryNew("SLAB_PATIO"),

  FOOTING: selectEnabledSubCategoryNew("FOOTING"),
  BRICKLEDGE_CUT: selectEnabledSubCategoryNew("BRICKLEDGE_CUT"),
  BRICKLEDGE_PROJECTED: selectEnabledSubCategoryNew("BRICKLEDGE_PROJECTED"),
  FOUNDATION_CURB: selectEnabledSubCategoryNew("FOUNDATION_CURB"),
  FOUNDATION_DASHED_LINEWORK: selectEnabledSubCategoryNew("FOUNDATION_DASHED_LINEWORK"),
  FOUNDATION_SOLID_LINEWORK: selectEnabledSubCategoryNew("FOUNDATION_SOLID_LINEWORK"),

  STEM_WALL_UNIONED_EDGES: selectEnabledSubCategoryNew("STEM_WALL_UNIONED_EDGES"),
  STEM_WALL_ABOVE: selectEnabledSubCategoryNew("STEM_WALL_ABOVE"),
  STEM_WALL_ABOVE_GAPS: selectEnabledSubCategoryNew("STEM_WALL_ABOVE_GAPS"),
  STEM_WALL_BELOW: selectEnabledSubCategoryNew("STEM_WALL_BELOW"),

  PIER: selectEnabledSubCategoryNew("PIER"),
  PIER_HIDDEN: selectEnabledSubCategoryNew("PIER_HIDDEN"),

  STAIR: selectEnabledSubCategoryNew("STAIR"),
  STAIR_ABOVE: selectEnabledSubCategoryNew("STAIR_ABOVE"),
  STAIR_BREAK_LINE: selectEnabledSubCategoryNew("STAIR_BREAK_LINE"),
  STAIR_PROJECTED: selectEnabledSubCategoryNew("STAIR_PROJECTED"),

  WALL_EXTERIOR_DIM_LABEL: styleEnabledSubCategoryNew("WALL_EXTERIOR_DIM_LABEL"),
  WALL_EXTERIOR_DIM_LINEWORK: styleEnabledSubCategoryNew("WALL_EXTERIOR_DIM_LINEWORK"),
  WALL_EXTERIOR_DIM_TICK: styleEnabledSubCategoryNew("WALL_EXTERIOR_DIM_TICK"),
  WALL_EXTERIOR_DIM_WITNESS: styleEnabledSubCategoryNew("WALL_EXTERIOR_DIM_WITNESS"),

  WALL_INTERIOR_DIM_LABEL: styleEnabledSubCategoryNew("WALL_INTERIOR_DIM_LABEL"),
  WALL_INTERIOR_DIM_LINEWORK: styleEnabledSubCategoryNew("WALL_INTERIOR_DIM_LINEWORK"),
  WALL_INTERIOR_DIM_TICK: styleEnabledSubCategoryNew("WALL_INTERIOR_DIM_TICK"),
  WALL_INTERIOR_DIM_WITNESS: styleEnabledSubCategoryNew("WALL_INTERIOR_DIM_WITNESS"),

  FOUNDATION_DIM_LABEL: styleEnabledSubCategoryNew("FOUNDATION_DIM_LABEL"),
  FOUNDATION_DIM_LINEWORK: styleEnabledSubCategoryNew("FOUNDATION_DIM_LINEWORK"),
  FOUNDATION_DIM_TICK: styleEnabledSubCategoryNew("FOUNDATION_DIM_TICK"),
  FOUNDATION_DIM_WITNESS: styleEnabledSubCategoryNew("FOUNDATION_DIM_WITNESS"),

  ROOF_OVERHANG_DIM_LABEL: styleEnabledSubCategoryNew("ROOF_OVERHANG_DIM_LABEL"),
  ROOF_OVERHANG_DIM_LINEWORK: styleEnabledSubCategoryNew("ROOF_OVERHANG_DIM_LINEWORK"),
  ROOF_OVERHANG_DIM_TICK: styleEnabledSubCategoryNew("ROOF_OVERHANG_DIM_TICK"),
  ROOF_OVERHANG_DIM_WITNESS: styleEnabledSubCategoryNew("ROOF_OVERHANG_DIM_WITNESS"),
  ROOF_OVERHANG_DIM_LINE: undefined,
  ROOF_OVERHANG_DIM_GROUP: undefined,

  ELECTRICAL_CONTROLLERS: selectEnabledSubCategoryNew("ELECTRICAL_CONTROLLERS"),
  ELECTRICAL_LINKS: selectEnabledSubCategoryNew("ELECTRICAL_LINKS"),
  ELECTRICAL_LOADS: selectEnabledSubCategoryNew("ELECTRICAL_LOADS"),
  ELECTRIC_LOAD_LABEL: selectEnabledSubCategoryNew("ELECTRIC_LOAD_LABEL"),

  PROP_APPLIANCES: selectEnabledSubCategoryNew("PROP_APPLIANCES"),
  PROP_FIXTURES: selectEnabledSubCategoryNew("PROP_FIXTURES"),
  PROP_FURNITURE: selectEnabledSubCategoryNew("PROP_FURNITURE"),
  PROP_OTHER: selectEnabledSubCategoryNew("PROP_OTHER"),
  PROP_FOLIAGE: selectEnabledSubCategoryNew("PROP_FOLIAGE"), // This has some special behavior - it's never rendered in linework views, but allowed in realistic style views.

  COLUMN: selectEnabledSubCategoryNew("COLUMN"),

  RAILING: selectEnabledSubCategoryNew("RAILING"),

  PORCH_COLUMNS: selectEnabledSubCategoryNew("PORCH_COLUMNS"),
  PORCH_BEAMS: selectEnabledSubCategoryNew("PORCH_BEAMS"),

  WALLS: selectEnabledSubCategoryNew("WALLS"),

  CASEWORK: selectEnabledSubCategoryNew("CASEWORK"),
  CASEWORK_SOLID_DASH: undefined,
  CASEWORK_POCHE: undefined,

  DOORS: selectEnabledSubCategoryNew("DOORS"),
  WINDOWS: selectEnabledSubCategoryNew("WINDOWS"),
  SKYLIGHTS: selectEnabledSubCategoryNew("SKYLIGHTS"),

  WAINSCOTING: selectEnabledSubCategoryNew("WAINSCOTING"),
  WAINSCOTING_CUT: styleEnabledSubCategoryNew("WAINSCOTING_CUT"),
  WAINSCOTING_PROJECTED: styleEnabledSubCategoryNew("WAINSCOTING_PROJECTED"),

  WINDOW_CASING_CUT: selectEnabledSubCategoryNew("WINDOW_CASING_CUT"),
  WINDOW_CASING_PROJECTED: selectEnabledSubCategoryNew("WINDOW_CASING_PROJECTED"),
  DOOR_CASING_CUT: selectEnabledSubCategoryNew("DOOR_CASING_CUT"),
  DOOR_OPERATION_LINEWORK: selectEnabledSubCategoryNew("DOOR_OPERATION_LINEWORK"),

  ROOM_LABELS: selectEnabledSubCategoryNew("ROOM_LABELS"),
  ROOF: selectEnabledSubCategoryNew("ROOF"),

  PERIMS_WALL: selectEnabledSubCategoryNew("PERIMS_WALL"),
  FLOOR_HEIGHT_DIFFERENCE: selectEnabledSubCategoryNew("FLOOR_HEIGHT_DIFFERENCE"),
  PERIMS_FLOORPLATE: selectEnabledSubCategoryNew("PERIMS_FLOORPLATE"),

  FLOOR_FILL: undefined,
  PROP_FILL: undefined,
  FLOOR_AREA_CONDITIONED: selectEnabledSubCategoryNew("FLOOR_AREA_CONDITIONED"),
  FLOOR_AREA_UNCONDITIONED: selectEnabledSubCategoryNew("FLOOR_AREA_UNCONDITIONED"),
  FLOOR_FINISH: selectEnabledSubCategoryNew("FLOOR_FINISH"),
  FLOOR_FRAMING: selectEnabledSubCategoryNew("FLOOR_FRAMING"),
  FLOOR_FRAMING_LABEL: selectEnabledSubCategoryNew("FLOOR_FRAMING_LABEL"),
  FLOOR_SYSTEM_TAG: selectEnabledSubCategoryNew("FLOOR_SYSTEM_TAG"),

  TOP_OF_PLATE_MARKERS: selectEnabledSubCategoryNew("TOP_OF_PLATE_MARKERS"),
  TOP_OF_PLATE_DIMENSIONS: selectEnabledSubCategoryNew("TOP_OF_PLATE_DIMENSIONS"),
  WINDOW_HEADER_ANNOTATION: selectEnabledSubCategoryNew("WINDOW_HEADER_ANNOTATION"),
  ELEVATION_MARKERS: selectEnabledSubCategoryNew("ELEVATION_MARKERS"),
  DORMER_WINDOW_VERTICAL_DIMENSIONS: selectEnabledSubCategoryNew("DORMER_WINDOW_VERTICAL_DIMENSIONS"),
  DORMER_WINDOW_TAGS: selectEnabledSubCategoryNew("DORMER_WINDOW_TAGS"),
  ROOF_TRUNCATION_HEIGHT_DIMENSIONS: selectEnabledSubCategoryNew("ROOF_TRUNCATION_HEIGHT_DIMENSIONS"),
  ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS: selectEnabledSubCategoryNew("ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS"),
  ROOF_WIDTH_DIMENSIONS: selectEnabledSubCategoryNew("ROOF_WIDTH_DIMENSIONS"),
  ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS: selectEnabledSubCategoryNew("ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS"),
  ROOF_GABLE_WINDOW_TAGS: selectEnabledSubCategoryNew("ROOF_GABLE_WINDOW_TAGS"),
  ROOF_DIM_ANNOTATION: selectEnabledSubCategoryNew("ROOF_DIM_ANNOTATION"),
  ROOF_SLOPE_SYMBOL: selectEnabledSubCategoryNew("ROOF_SLOPE_SYMBOL"),
  MATERIAL_TAGS: selectEnabledSubCategoryNew("MATERIAL_TAGS"),
  GRADE_LINE: selectEnabledSubCategoryNew("GRADE_LINE"),

  CONDITION_TAG: selectEnabledSubCategoryNew("CONDITION_TAG"),
  CEILING: selectEnabledSubCategoryNew("CEILING"),

  RAILING_PROJECTED: selectEnabledSubCategoryNew("RAILING_PROJECTED"),
  RAILING_ABOVE: selectEnabledSubCategoryNew("RAILING_ABOVE"),
  RAILING_POST: selectEnabledSubCategoryNew("RAILING_POST"),

  RAILING_PROJECTED_STAIR_ONLY: selectEnabledSubCategoryNew("RAILING_PROJECTED_STAIR_ONLY"),
  RAILING_ABOVE_STAIR_ONLY: selectEnabledSubCategoryNew("RAILING_ABOVE_STAIR_ONLY"),
  RAILING_POST_STAIR_ONLY: selectEnabledSubCategoryNew("RAILING_POST_STAIR_ONLY"),

  PORCH: selectEnabledSubCategoryNew("PORCH"),
  DECK: undefined,
  WALL_BOUNDARIES: selectEnabledSubCategoryNew("WALL_BOUNDARIES"),
  WALL_CORE: selectEnabledSubCategoryNew("WALL_CORE"),
  WALL_SUBSTRATE: selectEnabledSubCategoryNew("WALL_SUBSTRATE"),
  WALL_EXTERIOR_FINISH: selectEnabledSubCategoryNew("WALL_EXTERIOR_FINISH"),
  WALL_EXTERIOR_FINISH_PROJECTED: selectEnabledSubCategoryNew("WALL_EXTERIOR_FINISH_PROJECTED"),
  STAIR_WALLS: selectEnabledSubCategoryNew("STAIR_WALLS"),

  CHASE_ROOMS: undefined,

  PROP_TAG: selectEnabledSubCategoryNew("PROP_TAG"),
  WINDOW_TAGS: selectEnabledSubCategoryNew("WINDOW_TAGS"),
  DOOR_TAGS: selectEnabledSubCategoryNew("DOOR_TAGS"),
  HEADER_FRAMING_TAGS: selectEnabledSubCategoryNew("HEADER_FRAMING_TAGS"),
  CASEWORK_TAG: selectEnabledSubCategoryNew("CASEWORK_TAG"),
  OPENING_TAGS: selectEnabledSubCategoryNew("OPENING_TAGS"),

  ROOF_PERIMS: selectEnabledSubCategoryNew("ROOF_PERIMS"),
  ROOF_PERIMS_WITH_LEVEL_PERIMS: selectEnabledSubCategoryNew("ROOF_PERIMS_WITH_LEVEL_PERIMS"),
  ROOF_RIDGE_VENT: selectEnabledSubCategoryNew("ROOF_RIDGE_VENT"),
  ROOF_DORMER_FRAMING: undefined,
  ROOF_SLOPE_ANNOS: selectEnabledSubCategoryNew("ROOF_SLOPE_ANNOS"),
  ROOF_SKYLIGHT_ANNOS: selectEnabledSubCategoryNew("ROOF_SKYLIGHT_ANNOS"),
  ROOF_RIDGE_VENT_ANNOS: selectEnabledSubCategoryNew("ROOF_RIDGE_VENT_ANNOS"),
  ROOF_FRAMING: selectEnabledSubCategoryNew("ROOF_FRAMING"),

  SLAB: selectEnabledSubCategoryNew("SLAB"),
  STEM_WALL: selectEnabledSubCategoryNew("STEM_WALL"),

  SITE_SHAPE_CONTOURS: selectEnabledSubCategoryNew("SITE_SHAPE_CONTOURS"),
  SITE_SHAPE_ELEVATION_TAGS: selectEnabledSubCategoryNew("SITE_SHAPE_ELEVATION_TAGS"),
  SITE_SHAPE_DRIVEWAYS: selectEnabledSubCategoryNew("SITE_SHAPE_DRIVEWAYS"),
  SITE_SHAPE_ROADS: selectEnabledSubCategoryNew("SITE_SHAPE_ROADS"),

  FOUNDATION_OUTLINE: selectEnabledSubCategoryNew("FOUNDATION_OUTLINE"),

  FOUNDATION_FOOTING: selectEnabledSubCategoryNew("FOUNDATION_FOOTING"),
  FOUNDATION_PIER: selectEnabledSubCategoryNew("FOUNDATION_PIER"),
  TRIM: selectEnabledSubCategoryNew("TRIM"),
  ROOF_FRIEZE_BOARD: selectEnabledSubCategoryNew("ROOF_FRIEZE_BOARD"),
  ROOF_FASCIA_BOARD: selectEnabledSubCategoryNew("ROOF_FASCIA_BOARD"),
  ROOF_SOFFIT: selectEnabledSubCategoryNew("ROOF_SOFFIT"),
  ROOF_FASCIA_TOP_TRIM: selectEnabledSubCategoryNew("ROOF_FASCIA_TOP_TRIM"),
  ROOF_BELLY_BAND_BOARD: selectEnabledSubCategoryNew("ROOF_BELLY_BAND_BOARD"),
  ROOF_WALL_BASE_BOARD: selectEnabledSubCategoryNew("ROOF_WALL_BASE_BOARD"),
  ROOF_EXPOSED_RAFTER: selectEnabledSubCategoryNew("ROOF_EXPOSED_RAFTER"),
  ROOF_GABLE_VENT: selectEnabledSubCategoryNew("ROOF_GABLE_VENT"),
  CORNER_BOARD: selectEnabledSubCategoryNew("CORNER_BOARD"),
  WALL_FRAMING: selectEnabledSubCategoryNew("WALL_FRAMING"),
  SUB_FLOORING: selectEnabledSubCategoryNew("SUB_FLOORING"),

  RELAXATION_DEBUG: selectEnabledSubCategoryNew("RELAXATION_DEBUG"),

  SECTION_VIEW_CALLOUT: selectEnabledSubCategoryNew("SECTION_VIEW_CALLOUT"),

  UNDEFINED: undefined,
  UTILITY_FOUNDATION_LINEWORK: undefined,
  ANNOT_DIM_GROUP: undefined,
  ANNOT_DIM_LINE: undefined,
  DIM_LABEL: undefined,
  DIM_LINE: undefined,
  DIM_LINEWORK: undefined,
  DIM_TICK: undefined,
  DIM_WITNESS: undefined,
  DIM_GROUP: undefined,
  FOUNDATION_DIM_LINE: undefined,
  FOUNDATION_DIM_GROUP: undefined,
  UTILITY_DIM_LABEL: undefined,
  UTILITY_DIM_LINEWORK: undefined,
  UTILITY_DIM_TICK: undefined,
  UTILITY_DIM_WITNESS: undefined,
  WALL_INTERIOR_DIM_LINE: undefined,
  WALL_INTERIOR_DIM_GROUP: undefined,
  WALL_EXTERIOR_DIM_LINE: undefined,
  WALL_EXTERIOR_DIM_GROUP: undefined,
  INTERIOR_LABELS: undefined,
  ALL_LVL_ANNOT_LINEWORK: undefined,
  ALL_LVL_ANNOT_NOTE: undefined,
  ALL_LVL_ANNOT_DETAIL: undefined,
  ALL_LVL_ANNOT_DIM_GROUP: undefined,
  ALL_LVL_ANNOT_DIM_LABEL: undefined,
  ALL_LVL_ANNOT_DIM_LINE: undefined,
  ALL_LVL_ANNOT_DIM_LINEWORK: undefined,
  ALL_LVL_ANNOT_DIM_TICK: undefined,
  ALL_LVL_ANNOT_DIM_WITNESS: undefined,
  DEBUG_ELEVATION_COLLIDERS: undefined,
  DEBUG_ELEVATION_NOTE_ENTITIES: undefined,
} as const;
